// frontend/src/App.js

import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import PropTypes from 'prop-types';
import { setNavigate } from './utils/navigation';
import { roleHasPermission, ADMIN_PANEL_ROLES } from './utils/permissions';
import { NotificationProvider } from './context/NotificationContext';

// Context Providers and Hooks from consolidated file
import { AppProvider, AuthProvider, CartProvider, useAuth } from './context';

// Scrolls to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [pathname]);
  return null;
};


// Common Components
import Layout from './components/common/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';

// Pages
import HomePage from './components/customer/HomePage2';
import ErrorPage from './pages/ErrorPage';
import RouterSafeErrorPage from './pages/RouterSafeErrorPage';
import GalleryPage from './pages/GalleryPage';
import NotFound from './pages/NotFound';
import MaintenancePage from './pages/MaintenancePage';
import OAuthCallback from './pages/auth/OAuthCallback';

// Lazy load components
const AboutPage = lazy(() => import('./components/customer/AboutPage'));
const ContactPage = lazy(() => import('./components/customer/ContactPage'));
const MenuPage = lazy(() => import('./components/customer/MenuPage'));
const ReservationPage = lazy(() => import('./components/customer/ReservationPage'));
const OrderConfirmation = lazy(() => import('./components/customer/OrderConfirmation'));
const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));
const CartPage = lazy(() => import('./pages/CartPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const ProfilePage = lazy(() => import('./pages/user/ProfilePage'));
const MyBookingsPage = lazy(() => import('./pages/user/MyBookingsPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const PaymentReturn = lazy(() => import('./pages/payment/PaymentReturn'));
const PaymentStubConfirm = lazy(() => import('./pages/payment/StubConfirm'));
const OrderTrackerPage = lazy(() => import('./pages/OrderTrackerPage'));

// Analytics Route Tracker
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    console.log(`Navigated to: ${location.pathname}`);
  }, [location]);

  return null;
};

// Bind react-router's navigate to the global navigation util so
// non-React code (services/index.js interceptor) can route cleanly
// instead of doing a full window.location.href reload.
const NavigationBinder = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
};

// Loading component for Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <LoadingSpinner size="large" />
    <p className="ml-4 text-gray-600 text-lg">Loading page...</p>
  </div>
);

// Protected Route Component - shows a toast explaining the redirect.
// Supports three orthogonal gates: requireAdmin, requirePermission (string),
// and requireAdminPanel (any admin-side role: admin/manager/staff/cashier/
// chef/waiter — used to gate /admin/* without forcing strict admin).
const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requirePermission = null,
  requireAdminPanel = false,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const toastShownRef = useRef(false);

  // Decide what (if anything) is wrong with this access attempt
  const blockReason = (() => {
    if (loading) return null;
    if (!user) return 'auth';
    if (requireAdmin && user.role !== 'admin') return 'admin';
    if (requireAdminPanel && !ADMIN_PANEL_ROLES.includes(user.role)) return 'admin';
    if (requirePermission && !roleHasPermission(user.role, requirePermission)) return 'permission';
    return null;
  })();

  // Fire a toast once per redirect so the user knows WHY they were bounced
  useEffect(() => {
    if (!blockReason || toastShownRef.current) return;
    toastShownRef.current = true;

    if (blockReason === 'auth') {
      toast('Please log in to continue.', {
        toastId: 'auth-required',
        autoClose: 4000,
      });
    } else if (blockReason === 'admin') {
      toast.error('Admin access required to view that page.', {
        toastId: 'admin-required',
        autoClose: 4000,
      });
    } else if (blockReason === 'permission') {
      toast.error("You don't have permission to view that page.", {
        toastId: 'permission-required',
        autoClose: 4000,
      });
    }
  }, [blockReason]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (blockReason === 'auth') {
    // Preserve where the user was trying to go so login can send them back
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location, reason: 'auth_required' }}
      />
    );
  }

  if (blockReason === 'admin') {
    return <Navigate to="/" replace />;
  }

  if (blockReason === 'permission') {
    // Bounce back to the admin dashboard rather than the public home —
    // a logged-in admin/manager landing on / is jarring.
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requireAdmin: PropTypes.bool,
  requirePermission: PropTypes.string,
  requireAdminPanel: PropTypes.bool,
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (user) {
    // Admin-side roles (admin/manager/staff/cashier/chef/waiter) land on
    // the admin panel; regular customers land on the public home.
    if (ADMIN_PANEL_ROLES.includes(user.role)) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};


// Main App Component
function App() {
  // App Routes Component - MOVED INSIDE App function to be within AuthProvider context
  const AppRoutes = () => {
    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
    const { user } = useAuth();

    // Check maintenance mode
    if (isMaintenanceMode && (!user || user.role !== 'admin')) {
      return <MaintenancePage />;
    }

    return (
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/menu/:tableNumber" element={<MenuPage />} />
            <Route path="/table/:tableNumber" element={<MenuPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/newsletter/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/payment/return" element={<PaymentReturn />} />
            <Route path="/payment/stub-confirm/:id" element={<PaymentStubConfirm />} />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              }
            />

            {/* Protected Customer Routes */}
            {/* Reservations are guest-friendly — same model as Orders.
                Backend POST /reservations is optionalAuth, the form
                collects name/email/phone, and a logged-in user's profile
                pre-fills inside the page (parity with the cart). */}
            <Route path="/reservations" element={<ReservationPage />} />
            {/* Order confirmation — PUBLIC so guests aren't bounced to
                login after checkout. Order data arrives via router state. */}
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-bookings"
              element={
                <ProtectedRoute>
                  <MyBookingsPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requireAdminPanel>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute requireAdminPanel>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute requireAdminPanel>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/profile"
              element={
                <ProtectedRoute requireAdminPanel>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requireAdminPanel>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            {/* Other admin tabs share the same AdminPanel — the panel reads
                the URL via useLocation to pick which tab to render. */}
            {['/admin/kitchen', '/admin/floor', '/admin/menu', '/admin/orders', '/admin/reservations', '/admin/tables', '/admin/invoices',
              '/admin/reports', '/admin/stocks', '/admin/expenses', '/admin/staff', '/admin/activity', '/admin/site', '/admin/newsletter'].map(p => (
              <Route
                key={p}
                path={p}
                element={
                  <ProtectedRoute requireAdminPanel>
                    <AdminPanel />
                  </ProtectedRoute>
                }
              />
            ))}

            {/* Cart Route */}
            <Route path="/cart" element={<CartPage />} />

            {/* Public order tracker — link sent in confirmation emails. */}
            <Route path="/track/:orderNumber" element={<OrderTrackerPage />} />

            {/* 404 - Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    );
  };

  return (
    <ErrorBoundary fallback={<RouterSafeErrorPage />}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AppProvider>
          <AuthProvider>
            <CartProvider>
              <NotificationProvider>
              <div className="App">
                <ScrollToTop />
                <Toaster
                  position="top-right"
                  gutter={8}
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#0D0D0D',
                      color: '#E2D0B0',
                      border: '1px solid rgba(196,162,90,0.25)',
                      borderRadius: '0px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
                      fontFamily: '"Inter", system-ui, sans-serif',
                      fontSize: '0.8rem',
                      letterSpacing: '0.04em',
                      padding: '14px 18px',
                      maxWidth: '360px',
                    },
                    success: {
                      duration: 3500,
                      style: {
                        background: '#0D0D0D',
                        color: '#E2D0B0',
                        border: '1px solid rgba(196,162,90,0.4)',
                        borderLeft: '3px solid #C4A25A',
                      },
                      iconTheme: {
                        primary: '#C4A25A',
                        secondary: '#0D0D0D',
                      },
                    },
                    error: {
                      duration: 5000,
                      style: {
                        background: '#0D0D0D',
                        color: '#E2D0B0',
                        border: '1px solid rgba(220,80,80,0.35)',
                        borderLeft: '3px solid #c0392b',
                      },
                      iconTheme: {
                        primary: '#c0392b',
                        secondary: '#0D0D0D',
                      },
                    },
                  }}
                />
                <RouteTracker />
                <NavigationBinder />
                <AppRoutes />
              </div>
              </NotificationProvider>
            </CartProvider>
          </AuthProvider>
        </AppProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;